/**
 * AgreementTypeStep - First step asking how user wants to create agreement
 *
 * Intent-first approach: Ask what kind of agreement before showing any fields
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChoiceCard, ChoiceGrid, StepContainer } from '../FlowComponents';

const AGREEMENT_TYPES = [
  {
    id: 'ai',
    title: 'AI Generated',
    description: 'Describe your service and let AI create a professional agreement',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    popular: true,
  },
  {
    id: 'template',
    title: 'From Template',
    description: 'Choose from professional templates designed for photographers',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
      </svg>
    ),
  },
  {
    id: 'manual',
    title: 'Write My Own',
    description: 'Start with a blank canvas and write your agreement from scratch',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  },
  {
    id: 'duplicate',
    title: 'Copy Previous',
    description: 'Duplicate an existing agreement and modify it for a new client',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
];

const AgreementTypeStep = ({ data, updateData, goNext }) => {
  const [selectedType, setSelectedType] = useState(data.agreementType || null);

  const handleSelect = (typeId) => {
    setSelectedType(typeId);
    updateData({ agreementType: typeId });
  };

  const handleContinue = () => {
    if (selectedType) {
      goNext();
    }
  };

  return (
    <StepContainer>
      <div className="max-w-2xl mx-auto">
        <ChoiceGrid columns={2}>
          {AGREEMENT_TYPES.map((type) => (
            <ChoiceCard
              key={type.id}
              title={type.title}
              description={type.description}
              icon={type.icon}
              selected={selectedType === type.id}
              onClick={() => handleSelect(type.id)}
              badge={type.popular ? 'Recommended' : null}
            />
          ))}
        </ChoiceGrid>

        {/* Continue button */}
        {selectedType && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 flex justify-center"
          >
            <button
              onClick={handleContinue}
              className="px-8 py-3 bg-brand hover:bg-brand-hover text-black font-medium rounded-2xl transition-colors"
            >
              Continue
            </button>
          </motion.div>
        )}
      </div>
    </StepContainer>
  );
};

export default AgreementTypeStep;
